import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useCallback, useEffect, useState } from 'react';
import { Modal, Tooltip, Button, App, } from 'antd';
import { DeleteOutlined, ExclamationCircleFilled, LockOutlined, } from '@ant-design/icons';
import IconButton from 'components/IconButton';
import { txt } from 'libs/i18n';
import * as ProjectAPI from 'api/project-api';
import { useSession } from 'components/Authenticated';
import { useBeeyTutorial } from 'screens/DashboardScreen/beeyTutorial';
import { useHistory } from 'react-router';
import { useSignals } from '@preact/signals-react/runtime';
import { computed } from '@preact/signals-react';
import StatusBar from '../StatusBar';
import styles from './style.module.less';
import SavingStatus from './SavingStatus';
import ProjectNameInput from './ProjectNameInput';
import ProjectRating from './ProjectRating';
import { useEditorContext } from '../contexts/projectSignal';
import ShareSection from './ShareSection';
const MemoizedEditorToolbar = ({ onProjectUpdated, progressStatus, projectErrorStatus, onStopTranscription, enqueueProjectUpdate, }) => {
    const { projectSignal } = useEditorContext();
    const { retrieveTutorialState } = useBeeyTutorial();
    const { session } = useSession();
    const { notification, message } = App.useApp();
    const history = useHistory();
    useSignals();
    const projectReadOnly = computed(() => projectSignal.value.isReadOnly).value;
    const projectDeleteAt = computed(() => projectSignal.value.deleteAt).value;
    const isTeamProject = computed(() => projectSignal.value.isTeamProject).value;
    const canDeleteProject = session.login.hasClaim('team:deleteProjects');
    const canShowDeleteButton = (!isTeamProject || canDeleteProject)
        && progressStatus === 'not-in-progress'
        && projectDeleteAt === undefined;
    const [deleteModalVisible, setDeleteModalVisible] = useState(false);
    const [projectDeleting, setProjectDeleting] = useState(false);
    const displayDemoProjectWarning = () => {
        const handleOnClick = () => {
            notification.destroy('trialProjectMessageTextLink');
            history.push('/account');
        };
        notification.warning({
            message: (txt('trialProjectMessageTitle')),
            description: (_jsxs(_Fragment, { children: [`${txt('trialProjectMessageContent')} `, _jsx(Button, { type: "link", onClick: handleOnClick, children: txt('trialProjectMessageTextLink') })] })),
            duration: 0,
            key: 'trialProjectMessageTextLink',
        });
    };
    useEffect(() => {
        if (!session.login.hasClaim('paidFeatures:enabled') && retrieveTutorialState() !== 'running') {
            displayDemoProjectWarning();
        }
    }, []);
    const handleProjectNameChanged = useCallback(async (newName) => {
        await enqueueProjectUpdate(async (initialProject) => {
            const updateResult = await ProjectAPI.editProjectDescription(session.connection, initialProject.id, {
                current: { name: initialProject.description.name },
                new: { name: newName },
            });
            if (updateResult.isSuccess()) {
                void message.success(txt('nameSaved'));
                return updateResult.get();
            }
            void message.error(`${txt('failedName')}`);
            throw Error(updateResult.err());
        });
    }, []);
    const deleteProject = async () => {
        setProjectDeleting(true);
        const result = await ProjectAPI.trashProject(session.connection, projectSignal.value);
        if (result.isSuccess()) {
            void message.success(`${projectSignal.value.description.name} ${txt('successfullyDeleted').toLowerCase()}`);
        }
        else {
            notification.error({
                message: txt('failedToDelete'),
                placement: 'topRight',
                duration: 10,
            });
        }
        setProjectDeleting(false);
        history.push('/projects');
    };
    return (_jsxs("div", { className: styles.editorToolbar, children: [_jsx(ProjectNameInput, { onProjectNameChange: handleProjectNameChanged }), _jsxs("div", { className: styles.buttons, children: [_jsx(ShareSection, { onProjectUpdated: onProjectUpdated }), canShowDeleteButton ? (_jsx(Tooltip, { title: txt('delete'), children: _jsx(IconButton, { id: "action-delete-project", danger: true, className: styles.deleteIcon, onClick: () => setDeleteModalVisible(true), children: _jsx(DeleteOutlined, {}) }) })) : null] }), _jsx(Modal, { className: styles.deleteProjectModal, open: deleteModalVisible, cancelText: txt('cancel'), okText: txt('yesDelete'), title: (_jsxs("div", { className: styles.deleteModalTitle, children: [_jsx(ExclamationCircleFilled, {}), txt('deleteProject')] })), okButtonProps: { danger: true, loading: projectDeleting }, onOk: () => deleteProject(), onCancel: () => setDeleteModalVisible(false), children: _jsx("p", { children: txt('deleteInfo') }) }), _jsx(StatusBar, { progressStatus: progressStatus, projectErrorStatus: projectErrorStatus, onStopTranscription: onStopTranscription }), _jsx(ProjectRating, { projectInProgress: progressStatus !== 'not-in-progress' }), projectReadOnly ? (_jsxs("div", { className: styles.readOnlyTag, children: [_jsx(LockOutlined, {}), _jsx("span", { className: styles.readOnlyTagText, children: txt('readOnly') })] }))
                : _jsx(SavingStatus, {})] }));
};
export default React.memo(MemoizedEditorToolbar);
